export { N as NodeHttpClientAdapter } from './chunks/node-http-client-DloDLfm9.js';

/**
 * Angular HTTP Client adapter for PERS SDK
 *
 * Simple bridge between Angular's HttpClient and PERS SDK's HttpClient interface.
 * Use this for easy integration with Angular applications without requiring Angular as a dependency.
 *
 * @example
 * ```typescript
 * import { inject } from '@angular/core';
 * import { HttpClient } from '@angular/common/http';
 * import { createPersSDK } from '@explorins/pers-sdk';
 * import { AngularHttpClientAdapter } from '@explorins/pers-sdk/platform-adapters';
 *
 * const sdk = createPersSDK(
 *   new AngularHttpClientAdapter(inject(HttpClient)),
 *   { environment: 'production', apiProjectKey: 'your-key' }
 * );
 *
 * // Use managers directly
 * await sdk.user.getCurrentUser();
 * ```
 */
class AngularHttpClientAdapter {
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    async get(url, options = {}) {
        const mergedOptions = this.mergeOptions(options);
        const request$ = this.httpClient.get(url, mergedOptions);
        return this.convertObservableToPromise(request$);
    }
    async post(url, data = null, options = {}) {
        const mergedOptions = this.mergeOptions(options);
        const request$ = this.httpClient.post(url, data, mergedOptions);
        return this.convertObservableToPromise(request$);
    }
    async put(url, data = null, options = {}) {
        const mergedOptions = this.mergeOptions(options);
        const request$ = this.httpClient.put(url, data, mergedOptions);
        return this.convertObservableToPromise(request$);
    }
    async delete(url, options = {}) {
        const mergedOptions = this.mergeOptions(options);
        const request$ = this.httpClient.delete(url, mergedOptions);
        return this.convertObservableToPromise(request$);
    }
    async patch(url, data = null, options = {}) {
        const mergedOptions = this.mergeOptions(options);
        const request$ = this.httpClient.patch(url, data, mergedOptions);
        return this.convertObservableToPromise(request$);
    }
    async convertObservableToPromise(observable) {
        // Dynamically import firstValueFrom to avoid build-time dependency
        const { firstValueFrom } = await import('rxjs');
        return firstValueFrom(observable);
    }
    mergeOptions(options) {
        // Create headers object without importing HttpHeaders
        const defaultHeaders = { 'Content-Type': 'application/json' };
        const headers = { ...defaultHeaders, ...options.headers };
        return {
            headers,
            observe: 'body',
            ...options
        };
    }
}

/**
 * Browser Fetch HTTP Client adapter for PERS SDK
 *
 * Simple fetch-based HTTP client for browser environments.
 * Works with vanilla JavaScript, React, Vue, or any browser-based framework.
 *
 * @example
 * ```typescript
 * import { createPersSDK } from '@explorins/pers-sdk';
 * import { BrowserFetchClientAdapter } from '@explorins/pers-sdk/platform-adapters';
 *
 * const sdk = createPersSDK(
 *   new BrowserFetchClientAdapter(),
 *   { environment: 'production', apiProjectKey: 'your-key' }
 * );
 *
 * // Use managers directly
 * await sdk.user.getCurrentUser();
 * ```
 */
class BrowserFetchClientAdapter {
    async get(url, options = {}) {
        return this.request(url, 'GET', undefined, options);
    }
    async post(url, data = null, options = {}) {
        return this.request(url, 'POST', data, options);
    }
    async put(url, data = null, options = {}) {
        return this.request(url, 'PUT', data, options);
    }
    async delete(url, options = {}) {
        return this.request(url, 'DELETE', undefined, options);
    }
    async patch(url, data = null, options = {}) {
        return this.request(url, 'PATCH', data, options);
    }
    async request(url, method, data, options = {}) {
        const headers = {
            'Content-Type': 'application/json',
            ...options.headers
        };
        const fetchOptions = {
            method,
            headers,
            ...options
        };
        if (data !== undefined && method !== 'GET') {
            fetchOptions.body = JSON.stringify(data);
        }
        const response = await fetch(url, fetchOptions);
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        // Handle different response types
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
            return response.json();
        }
        return response.text();
    }
}

export { AngularHttpClientAdapter, BrowserFetchClientAdapter };
//# sourceMappingURL=platform-adapters.js.map
